// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CmsEditingMode from "@hardwoods/App/Components/CMS/CmsEditingMode";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LinkIcon from "@insite/mobius/Icons/Link";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC, useContext } from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasShellContext;

interface MyAccountDetailsLayoutStyle {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    logoWrapper?: InjectableCss;
    linksWrapper?: InjectableCss;
    brandsInfoWrapper?: InjectableCss;
    brandsImageWrapper?: InjectableCss;
    productLink?: LinkPresentationProps;
    externalLink?: LinkPresentationProps;
}

const styles: MyAccountDetailsLayoutStyle = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 40px;
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: center;
            ${xScreen.lte("xs")} {
                order: 1;
                flex-direction: column;
                justify-content: center;
                margin-bottom: 20px;
            }
        `,
    },
    linksWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
            margin-bottom: 20px;
            ${xScreen.lte("xs")} {
                order: 2;
                justify-content: flex-start;
            }
        `,
    },
    logoWrapper: {
        css: css`
            width: 150px;
            max-height: 250px;
            flex-grow: 0;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            margin-right: 25px;
            ${xScreen.lte("xs")} {
                margin-right: 0;
            }
        `,
    },
    brandsInfoWrapper: {
        css: css`
            flex: 1;
            display: flex;
            align-items: center;
            margin-right: 25px;
            min-width: 240px;
            ${xScreen.lte("xs")} {
                margin-right: 0;
            }
        `,
    },
    brandsImageWrapper: {
        css: css`
            width: 300px;
            max-height: 250px;
            flex-grow: 0;
            flex-shrink: 0;
            display: flex;
            align-items: center;
        `,
    },
    productLink: {
        icon: {
            iconProps: {
                src: ShoppingCart,
                color: "primary",
                size: 14,
            },
        },
        css: css`
            margin-right: 20px;
            margin-bottom: 10px;
        `,
    },
    externalLink: {
        icon: {
            iconProps: {
                src: LinkIcon,
                color: "primary",
                size: 14,
            },
        },
        css: css`
            margin-bottom: 10px;
        `,
    },
};

const BrandsDetailsLayout: FC<Props> = ({ id, shellContext }) => {
    const { value: brand } = useContext(BrandStateContext);
    if (!brand) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.linksWrapper}>
                <Link
                    data-test-selector="brandToProductPageLink"
                    href={brand.productListPagePath}
                    {...styles.productLink}
                >
                    {translate("Shop All {0} Products").replace("{0}", brand.name)}
                </Link>
                {brand.externalUrl && (
                    <Link data-test-selector="brandExternalUrlLink" href={brand.externalUrl} {...styles.externalLink}>
                        {translate("Visit {0} Website").replace("{0}", brand.name)}
                    </Link>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.container}>
                {(brand.logoLargeImagePath || shellContext.isEditing) && (
                    <StyledWrapper {...styles.logoWrapper}>
                        <CmsEditingMode editingChildren="Zone for Brands Logo">
                            <Zone contentId={id} zoneName="Content_BrandsLogo" />
                        </CmsEditingMode>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.brandsInfoWrapper}>
                    <CmsEditingMode editingChildren="Zone for Brands Info">
                        <Zone contentId={id} zoneName="Content_BrandsInfo" />
                    </CmsEditingMode>
                </StyledWrapper>
                {(brand.featuredImagePath || shellContext.isEditing) && (
                    <StyledWrapper {...styles.brandsImageWrapper}>
                        <CmsEditingMode editingChildren="Zone for Brands Image">
                            <Zone contentId={id} zoneName="Content_BrandsImage" />
                        </CmsEditingMode>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const categoryLayoutWidget: WidgetModule = {
    component: withIsInShell(BrandsDetailsLayout),
    definition: {
        group: "Hardwoods Layout" as any,
        displayName: "Brands Details Layout",
    },
};

export default categoryLayoutWidget;
